import { useSyncExternalStore } from 'react';

/** Minimal observable store: plain objects in, React re-renders out. */
export interface Store<T> {
  get(): T;
  set(next: T | ((prev: T) => T)): void;
  patch(partial: Partial<T>): void;
  subscribe(listener: () => void): () => void;
}

export function createStore<T extends object>(initial: T): Store<T> {
  let state = initial;
  const listeners = new Set<() => void>();
  const set: Store<T>['set'] = (next) => {
    const value = typeof next === 'function' ? (next as (p: T) => T)(state) : next;
    if (value === state) return;
    state = value;
    listeners.forEach((l) => l());
  };
  return {
    get: () => state,
    set,
    patch: (partial) => set({ ...state, ...partial }),
    subscribe: (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
  };
}

export function useStore<T extends object>(store: Store<T>): T;
export function useStore<T extends object, S>(store: Store<T>, select: (s: T) => S): S;
export function useStore<T extends object, S>(store: Store<T>, select?: (s: T) => S): T | S {
  return useSyncExternalStore(store.subscribe, () => (select ? select(store.get()) : store.get()));
}
